import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { App, Button, Card, Form, Input, Typography } from 'antd';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { useActiveOrg } from '../active-org-context';
import { orgsQueryKey } from '../active-org-context';
import { organizationsApi } from '../organizations.api';
import type { Organization } from '../types';

const schema = z.object({ name: z.string().trim().min(2, 'At least 2 characters').max(80) });
type FormValues = z.infer<typeof schema>;

export function CreateOrganizationPage() {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setActiveOrgId } = useActiveOrg();

  const form = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: { name: '' } });

  const mutation = useMutation({
    mutationFn: organizationsApi.create,
    onSuccess: (org) => {
      // Seed the new org into the cache synchronously so the dashboard doesn't
      // momentarily see an empty list and bounce us back to this screen. The
      // background refetch below reconciles with the server.
      queryClient.setQueryData<Organization[]>(orgsQueryKey, (prev) => {
        const list = prev ?? [];
        return list.some((o) => o.id === org.id) ? list : [...list, org];
      });
      setActiveOrgId(org.id);
      message.success(`Created ${org.name}`);
      void queryClient.invalidateQueries({ queryKey: orgsQueryKey });
      void navigate({ to: '/' });
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Failed to create organization'),
  });

  return (
    <Card style={{ maxWidth: 480, margin: '0 auto' }}>
      <Typography.Title level={4}>Create an organization</Typography.Title>
      <Typography.Paragraph type="secondary">
        Organizations are the top-level workspace. You'll be its owner.
      </Typography.Paragraph>
      <Form layout="vertical" onFinish={form.handleSubmit((v) => mutation.mutate(v))}>
        <Controller
          control={form.control}
          name="name"
          render={({ field, fieldState }) => (
            <Form.Item
              label="Organization name"
              validateStatus={fieldState.error ? 'error' : ''}
              help={fieldState.error?.message}
            >
              <Input {...field} placeholder="Acme Inc" autoFocus />
            </Form.Item>
          )}
        />
        <Button type="primary" htmlType="submit" block loading={mutation.isPending}>
          Create organization
        </Button>
      </Form>
    </Card>
  );
}
